import type { FC, ReactNode } from 'react'
import type { Heading } from '@/types'

import { List } from 'lucide-react'

import { cn } from '@/lib/utils'

interface TableOfContentsProps {
    headings: Heading[]
    className?: string
}

const Links: FC<{ headings: Heading[] }> = ({ headings }): ReactNode => (
    <ul className='mt-3 space-y-1.5'>
        {headings.map(heading => (
            <li key={heading.id} className={cn(heading.level === 3 && 'pl-4')}>
                <a
                    href={`#${heading.id}`}
                    className='block text-sm leading-snug text-muted-foreground transition-colors hover:text-foreground'
                >
                    {heading.text}
                </a>
            </li>
        ))}
    </ul>
)

export const TableOfContents: FC<TableOfContentsProps> = ({ headings, className }): ReactNode => {
    if (headings.length < 3) return null

    return (
        <nav aria-label='On this page' className={className}>
            <details
                open
                className='rounded-xl border border-border bg-secondary/40 p-4 lg:hidden [&_summary::-webkit-details-marker]:hidden'
            >
                <summary className='flex cursor-pointer list-none items-center gap-2 text-sm font-semibold'>
                    <List className='size-4 text-primary' />
                    On this page
                </summary>

                <Links headings={headings} />
            </details>

            <div className='hidden rounded-xl border border-border bg-secondary/40 p-4 lg:block'>
                <p className='flex items-center gap-2 text-sm font-semibold'>
                    <List className='size-4 text-primary' />
                    On this page
                </p>

                <Links headings={headings} />
            </div>
        </nav>
    )
}
